import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Dumbbell, Sparkles, Printer } from "lucide-react";
import { FITNESS_LEVELS, CLINICAL_CONSTRAINTS, computeExerciseZone } from "../lib/exerciseTypes";

export default function ExerciseHub() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [patient, setPatient] = useState(null);
  const [lang, setLang] = useState("mr");
  const [profile, setProfile] = useState({ age: "", weight_kg: "", height_cm: "", waist_cm: "", sex: "" });
  const [fitnessLevel, setFitnessLevel] = useState("beginner");
  const [constraints, setConstraints] = useState([]);
  const [planText, setPlanText] = useState("");
  const [planId, setPlanId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get(`/patients/${pid}`);
        const pt = p.data.patient;
        setPatient(pt);
        const v = (p.data.visits || [])[0] || {};
        const vt = v.vitals || {};
        setProfile({
          age: pt?.age || "",
          weight_kg: vt.weight || "",
          height_cm: vt.height || "",
          waist_cm: vt.waist || "",
          sex: pt?.sex || "",
        });
      } catch (e) {
        console.warn("load patient failed:", e);
        toast.error("Could not load patient");
      }
    })();
  }, [pid]);

  const metrics = useMemo(() => computeExerciseZone(profile), [profile]);
  const L = (en, mr) => (lang === "mr" ? mr : en);

  const toggle = (id) => setConstraints((cs) => (cs.includes(id) ? cs.filter((x) => x !== id) : [...cs, id]));

  const build = async () => {
    if (!metrics) { toast.error(L("Fill age, weight, height first", "आधी वय, वजन, उंची भरा")); return; }
    setLoading(true); setPlanText(""); setPlanId("");
    try {
      const r = await api.post("/exercise/workout-plan", {
        patient_id: pid,
        profile,
        preferences: { fitness_level: fitnessLevel, constraints, bmi: metrics.bmi, zone_en: metrics.zone_en },
        language: lang,
      });
      setPlanText(r.data?.plan?.plan_text || "");
      setPlanId(r.data?.plan?.plan_id || "");
      toast.success(L("Exercise plan generated & saved", "व्यायाम आराखडा तयार आणि जतन झाला"));
    } catch (e) {
      console.warn("exercise gen failed:", e);
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="exercise-back-btn"><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <div className="flex-1">
            <div className="text-xs text-slate-500 uppercase tracking-wider">{patient?.name} · {patient?.age} · {patient?.sex}</div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Dumbbell size={20} className="text-indigo-600" /> {L("Exercise Science Lab", "व्यायाम विज्ञान लॅब")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className={`px-3 py-1.5 text-xs rounded-md border ${lang === "en" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} onClick={() => setLang("en")} data-testid="exercise-lang-en">EN</button>
            <button className={`px-3 py-1.5 text-xs rounded-md border ${lang === "mr" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} onClick={() => setLang("mr")} data-testid="exercise-lang-mr">मराठी</button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <section className="col-span-12 lg:col-span-5 bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">{L("Metabolic Vitals", "मेटाबॉलिक जीवनसंख्या")}</div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[
                ["age", L("Age (Y)", "वय")],
                ["weight_kg", L("Weight (kg)", "वजन (किलो)")],
                ["height_cm", L("Height (cm)", "उंची (सेमी)")],
                ["waist_cm", L("Girth (cm)", "घेरे (सेमी)")],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-[9px] uppercase tracking-wider text-slate-500 block mb-0.5">{lbl}</label>
                  <Input type="number" value={profile[k]} onChange={(e) => setProfile((p) => ({ ...p, [k]: e.target.value }))} data-testid={`exercise-vital-${k}`} />
                </div>
              ))}
            </div>

            {metrics && (
              <div className="p-3 rounded-md bg-indigo-50 border border-indigo-200 mb-4 text-xs" data-testid="exercise-metrics">
                <div className="flex justify-between gap-2">
                  <span><span className="text-slate-500 text-[9px] uppercase mr-1">BMI</span> <span className="font-semibold text-indigo-800">{metrics.bmi}</span></span>
                  <span><span className="text-slate-500 text-[9px] uppercase mr-1">{L("Zone", "क्षेत्र")}</span> <span className="font-semibold text-indigo-800">{L(metrics.zone_en, metrics.zone_mr)}</span></span>
                </div>
              </div>
            )}

            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{L("Fitness Experience", "व्यायाम अनुभव")}</div>
            <div className="grid grid-cols-1 gap-1.5 mb-4">
              {FITNESS_LEVELS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFitnessLevel(f.id)}
                  className={`p-2.5 rounded-md border text-left ${fitnessLevel === f.id ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                  data-testid={`exercise-fitness-${f.id}`}
                >
                  <div className="text-xs font-semibold uppercase tracking-tight">{L(f.name_en, f.name_mr)}</div>
                  <div className={`text-[10px] mt-0.5 ${fitnessLevel === f.id ? "text-indigo-100" : "text-slate-500"}`}>{L(f.desc_en, f.desc_mr)}</div>
                </button>
              ))}
            </div>

            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{L("Physical Constraints", "शारीरिक मर्यादा")}</div>
            <div className="grid grid-cols-3 gap-1.5 mb-4">
              {CLINICAL_CONSTRAINTS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className={`p-2 rounded-md border text-center flex flex-col items-center gap-1 ${constraints.includes(c.id) ? "bg-rose-50 border-rose-500 text-rose-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`exercise-constraint-${c.id}`}
                >
                  <span className="text-lg">{c.icon}</span>
                  <span className="text-[9px] uppercase font-semibold leading-none">{L(c.name_en, c.name_mr)}</span>
                </button>
              ))}
            </div>

            <Button onClick={build} disabled={loading || !metrics} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="exercise-build-btn">
              <Sparkles size={14} className="mr-1" /> {loading ? L("Formulating…", "तयार होत आहे…") : L("Generate Tailor-Made Plan", "वैयक्तिक आराखडा तयार करा")}
            </Button>
          </section>

          <section className="col-span-12 lg:col-span-7">
            {planText ? (
              <div className="bg-white rounded-lg border border-indigo-200 shadow-sm p-5" data-testid="exercise-plan-output">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold flex items-center gap-1"><Dumbbell size={12} /> {L("Personalised workout", "वैयक्तिक व्यायाम")}</div>
                  {planId && (
                    <Button size="sm" variant="outline" onClick={() => window.open(`/exercise/print/${planId}`, "_blank")} data-testid="exercise-print-btn">
                      <Printer size={13} className="mr-1" /> {L("Print / Share", "प्रिंट / शेअर")}
                    </Button>
                  )}
                </div>
                <pre className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-800 font-sans">{planText}</pre>
                <div className="mt-4 bg-slate-900 text-white p-3 rounded text-[11px] italic leading-relaxed">
                  <div className="text-pink-300 uppercase text-[9px] tracking-widest mb-1 font-semibold">{L("Safety Note", "सुरक्षा सूचना")}</div>
                  {L('"Always carry glucose tablets and hydrate before starting."', '"व्यायाम सुरू करण्यापूर्वी नेहमी ग्लुकोजच्या गोळ्या जवळ ठेवा आणि पुरेसे पाणी प्या."')}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border-2 border-dashed border-slate-200 h-full min-h-[380px] flex flex-col items-center justify-center text-center p-8" data-testid="exercise-plan-empty">
                <Dumbbell size={40} className="text-slate-300 mb-3" />
                <div className="text-slate-500 font-semibold">{L("Awaiting profile data", "प्रोफाइल डेटाची प्रतीक्षा")}</div>
                <div className="text-xs text-slate-400 mt-2 max-w-xs">{L("Fill vitals + fitness experience + constraints on the left, then generate a custom movement protocol.", "डावीकडील माहिती भरून वैयक्तिक व्यायाम आराखडा तयार करा.")}</div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
